import { create } from 'zustand';
import { useAppStore } from './useAppStore';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QuestionAnswer = Record<string, any>;

export type QuestionItem = {
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  options: Record<string, any>;
  answers: Record<string, QuestionAnswer>;
};

type QuestionsList = Record<string, QuestionItem>;

type SetupArgs = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  questionsData: Record<string, any>;
  predifinedAnswers?: Record<string, Record<string, QuestionAnswer>> | null;
};

type AnswerArgs = {
  questionName: string;
  answer: QuestionAnswer;
};

type SetAnswerArgs = {
  questionName: string;
  answerName: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  answerValue: any;
};

type IsAnswerSelectedArgs = {
  questionName: string;
  answer: string;
};

type GetAnswersArgs = {
  questionName: string;
};

export type QuestionsState = {
  list: QuestionsList | null;
  savedAnswers: Record<string, Record<string, unknown>>;

  setQuestions: (list: QuestionsList) => void;
  saveAnswers: (answers: Record<string, Record<string, unknown>>) => void;

  setup: (args: SetupArgs) => void;
  addAnswer: (args: AnswerArgs) => void;
  addOnlyAnswer: (args: AnswerArgs) => void;
  setAnswer: (args: SetAnswerArgs) => void;
  removeAnswer: (args: AnswerArgs) => void;
  toggleAnswer: (args: AnswerArgs) => void;
  isAnswerSelected: (args: IsAnswerSelectedArgs) => QuestionAnswer | undefined;
  getAnswers: (args: GetAnswersArgs) => Record<string, QuestionAnswer> | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function createQuestion(name: string, details: any, predifinedAnswers: Record<string, Record<string, QuestionAnswer>> | null = null): QuestionItem {
  const savedAnswers = predifinedAnswers && predifinedAnswers[name];
  const age = useAppStore.getState().initialAge;
  const { options } = details;
  const newOptions = { ...options };
  // eslint-disable-next-line no-mixed-operators
  if (age && name === 'goal' || name === 'being_home_alone') {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    Object.entries(newOptions).forEach((item: [string, any]) => {
      if (item[1].age && age !== item[1].age) {
        delete newOptions[item[1].value];
      }
    });
  }

  return {
    name: name || '',
    options: newOptions,
    answers: details.answers || savedAnswers || {},
  };
}

function deriveSavedAnswers(list: QuestionsList): Record<string, Record<string, unknown>> {
  const out: Record<string, Record<string, unknown>> = {};
  Object.entries(list).forEach(([, item]) => {
    const answer = item.answers;
    Object.entries(answer).forEach(([, property]) => {
      if (property && typeof property === 'object') {
        if ('description' in property) delete property.description;
        if ('gender' in property) delete property.gender;
      }
    });
    out[item.name] = answer;
  });
  return out;
}

export const useQuestionsStore = create<QuestionsState>((set, get) => ({
  list: null,
  savedAnswers: {},

  setQuestions: (list) => set({ list }),
  saveAnswers: (answers) => set({ savedAnswers: answers }),

  setup: ({ questionsData, predifinedAnswers }) => {
    const questionsList: QuestionsList = {};
    Object.entries(questionsData).forEach(([name, details]) => {
      questionsList[name] = createQuestion(name, details, predifinedAnswers ?? null);
    });
    set({ list: questionsList });
  },

  addAnswer: ({ questionName, answer }) => {
    const list = { ...(get().list || {}) };
    if (!list[questionName]) return;
    list[questionName] = {
      ...list[questionName],
      answers: {
        ...list[questionName].answers,
        [answer.value]: { ...answer },
      },
    };
    set({ list, savedAnswers: deriveSavedAnswers(list) });
  },

  addOnlyAnswer: ({ questionName, answer }) => {
    const list = { ...(get().list || {}) };
    if (!list[questionName]) return;
    list[questionName] = {
      ...list[questionName],
      answers: { [answer.value]: { ...answer } },
    };
    set({ list, savedAnswers: deriveSavedAnswers(list) });
  },

  setAnswer: ({ questionName, answerName, answerValue }) => {
    const list = { ...(get().list || {}) };
    if (!list[questionName]) return;
    list[questionName] = {
      ...list[questionName],
      answers: {
        ...list[questionName].answers,
        [answerName]: answerValue,
      },
    };
    set({ list, savedAnswers: deriveSavedAnswers(list) });
  },

  removeAnswer: ({ questionName, answer }) => {
    const list = { ...(get().list || {}) };
    if (!list[questionName]) return;
    const newAnswers = { ...list[questionName].answers };
    delete newAnswers[answer.value];
    list[questionName] = { ...list[questionName], answers: newAnswers };
    set({ list, savedAnswers: deriveSavedAnswers(list) });
  },

  toggleAnswer: ({ questionName, answer }) => {
    const selected = get().isAnswerSelected({ questionName, answer: answer.value });
    if (!selected) {
      get().addAnswer({ questionName, answer });
    } else {
      get().removeAnswer({ questionName, answer });
    }
  },

  isAnswerSelected: ({ questionName, answer }) => {
    const { list } = get();
    return list?.[questionName]?.answers?.[answer];
  },

  getAnswers: ({ questionName }) => {
    const { list } = get();
    return (list?.[questionName]?.answers) || null;
  },
}));
